/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Button from 'carbon-react/lib/components/button';

import { UISystemIcon } from '@futrli/ui-components';
import {
  Logger,
  QUERY_PARAMS,
  ROUTES,
  getAllWorkspacesData,
  getIntegrationsOnboardingUrl,
  getReportingUrl,
  workspacesSelector,
  PREDICT_PRODUCT_ID
} from '@futrli/shared-services';
import {
  getOnboardingInitialSyncUrl,
  getOnboardingSageOrgsUrl
} from '@futrli/shared-services/onboarding/utils/route-utils';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { OnboardingNavbar } from './onboarding-navbar';
import * as styles from './onboarding.scss';
import { StoreState } from '../../core/redux-utils/types';
import { trackEvent } from '../../core/tracker';
import { TRACKING_EVENTS } from '../../core/constants';
import { OnboardingLoader } from './onboarding-loader';

export const FORECASTING_TYPE = 'forecasting';
export const REPORTING_TYPE = 'reporting';

export const OnboardingStart = () => {
  const [selectedOnboardingFlow, setSelectedOnboardingFlow] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const workspaceId = searchParams.get(QUERY_PARAMS.WORKSPACE_ID);
  const organisationId = searchParams.get(QUERY_PARAMS.ORG_ID);
  const datasourceId = searchParams.get(QUERY_PARAMS.DATASOURCE_ID);
  const status = searchParams.get(QUERY_PARAMS.STATUS);
  const connectedOrgs = searchParams.get(QUERY_PARAMS.CONNECTED_ORGS);
  const usedIntegration = searchParams.get(QUERY_PARAMS.INTEGRATION);

  const workspaces = useSelector((state: StoreState) => {
    if (!workspaceId) {
      return null;
    }
    const { normalised } = state;

    const workspaces = workspacesSelector(normalised) as WorkspacesListingItem[];

    return workspaces;
  });

  const dispatch = useDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        setIsLoading(true);
        await dispatch(getAllWorkspacesData());
      } catch (e) {
        Logger.error(e as string);
      } finally {
        setIsLoading(false);
      }
    };

    if (!workspaces) {
      fetchWorkspaces();
    }
  }, [dispatch, workspaces]);

  const handleNextClick = () => {
    if (!workspaceId) {
      return;
    }

    const onboardingType = selectedOnboardingFlow === 0 ? FORECASTING_TYPE : REPORTING_TYPE;

    if (onboardingType === FORECASTING_TYPE) {
      trackEvent(TRACKING_EVENTS.CHOSE_FORECASTING_ONBOARDING);
    } else {
      trackEvent(TRACKING_EVENTS.CHOSE_REPORTING_ONBOARDING);
    }

    if (usedIntegration === 'excel' && organisationId) {
      if (onboardingType === REPORTING_TYPE) {
        navigate(`${getReportingUrl(workspaceId, organisationId)}${ROUTES.ONBOARDING_ROUTE}`);
      } else {
        navigate(
          `${ROUTES.ONBOARDING_ROUTE}${ROUTES.ONBOARDING_INITIAL}${ROUTES.FORECAST_START}?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceId}&${QUERY_PARAMS.ORG_ID}=${organisationId}&${QUERY_PARAMS.INTEGRATION}=excel`
        );
      }
      return;
    }
    if (connectedOrgs && Number(connectedOrgs) > 1) {
      navigate(getOnboardingSageOrgsUrl(workspaceId, onboardingType));
    } else if (organisationId && datasourceId) {
      navigate(
        getOnboardingInitialSyncUrl(workspaceId, organisationId, datasourceId, onboardingType, usedIntegration || '')
      );
    }
  };

  const isSuccessfulConnection = status?.toLowerCase() === 'success';

  if (isLoading) {
    return <OnboardingLoader />;
  }

  if (!workspaceId || !status) {
    return null;
  }

  return (
    <>
      <OnboardingNavbar />
      <div className={styles.container}>
        <div style={{ height: 200 }} />
        <h1 className={styles.title} style={{ marginBottom: 30 }}>
          <FormattedMessage
            id="onboarding.what_do_you_want_to_do"
            defaultMessage="What do you want to do in Futrli, first?"
          />
        </h1>
        <div className={styles.subTitle} style={{ marginBottom: 40 }}>
          <FormattedMessage
            id="onboarding.help_personalise_experience"
            defaultMessage="Your choice helps personalise your first experience. You can still use every part of Futrli."
          />
        </div>
        <div style={{ display: 'flex' }}>
          <div
            className={classNames(styles.onboardingStartButton, {
              [styles.onboardingStartButtonActive]: selectedOnboardingFlow === 0
            })}
            onClick={() => setSelectedOnboardingFlow(0)}
          >
            <UISystemIcon iconName="create-forecast-start" width={40} height={40} />
            <FormattedMessage id="onboarding.create_budget_forecast" defaultMessage="Create a budget or forecast" />
          </div>
          <div
            className={classNames(styles.onboardingStartButton, {
              [styles.onboardingStartButtonActive]: selectedOnboardingFlow === 1
            })}
            style={{ marginLeft: 40 }}
            onClick={() => setSelectedOnboardingFlow(1)}
          >
            <UISystemIcon iconName="create-report-start" width={40} height={40} />
            <FormattedMessage
              id="onboarding.create_report_or_dashboard"
              defaultMessage="Create a report or dashboard"
            />
          </div>
        </div>
        <Button
          mt="30px"
          disabled={selectedOnboardingFlow === null || !isSuccessfulConnection}
          onClick={handleNextClick}
          buttonType="primary"
          size="large"
        >
          <FormattedMessage id="common.next" defaultMessage="Next" />
        </Button>
        {!isSuccessfulConnection && organisationId && (
          <div className={styles.demoText} style={{ marginTop: 40, textAlign: 'left', color: '#CD384B' }}>
            <FormattedMessage
              id="common.generic_system_error_with_link"
              defaultMessage="There has been a system error, please <link>Try again</link> or contact our support team for further assistance"
              values={{
                link: chunks => (
                  <Link
                    to={getIntegrationsOnboardingUrl(workspaceId, organisationId, PREDICT_PRODUCT_ID)}
                    style={{ color: '#CD384B', textDecoration: 'underline' }}
                  >
                    {chunks}
                  </Link>
                )
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};
